import coordtransform from 'coordtransform';
import env from '@/utils/env';
import lbsInitConfig from '@hb/map-api/es/lbsInitConfig';
import packageJson from '@/../package.json';
import lbsReGeoCodingSearch from '@hb/map-api/es/lbsReGeoCodingSearch';
import lbsDriveRouteByServer from '@hb/map-api/es/lbsDriveRouteByServer';

let _loadPromise;
let _cityInfoPromise;


export const LBS_CONFIG = {
  appKey: 'hitch_main_h',
  pKey: '3e5bbae53517f8b570aac944fd2375e9q',
};

// 用来初始化LBS模块
export const GAODE_WEB_API_KEY = '081864b4712b9436b07560551bc64d60';
// 用来初始化高德地图JS SDK
export const GAODE_JS_API_KEY = '1dedf329b1b6628e0fc484100d9abf3f';
export const GAODE_JS_API_SECURITY_CODE = 'a52b1182293d1f73a8f50ac8a7c6c673';


function initLBS() {
  lbsInitConfig({
    debug: false, // 默认false。开启之后会有关键信息提示，比如：逆地理缓存命中，小程序会有弹窗提示
    env: env.codeEnv,
    contextEnv: 'browser',
    context: window,
    gaode: { key: GAODE_WEB_API_KEY },
    lbs: { appKey: LBS_CONFIG.appKey, secretKey: LBS_CONFIG.pKey },
    UBT: window.TianQi.UBT,
    APM: window.TianQiApm.APM,
    extraInfo: {
      appName: packageJson.name,
      version: packageJson.version,
      systemCode: env.systemCode,
      from: 'browser',
      releaseVersion: '',
      __sysTag: '',
    },
  });
}

function loadMap() {
  if (!_loadPromise) {
    _loadPromise = new Promise((resolve, reject) => {
      window.initAMap = function initAMap() {
        resolve(window.AMap);
      };

      setTimeout(() => {
        reject(new Error('加载地图超时'));
      }, 3000);

      window._AMapSecurityConfig = {
        securityJsCode: GAODE_JS_API_SECURITY_CODE,
      };

      const mapUrl = `//webapi.amap.com/maps?v=1.4.15&key=${GAODE_JS_API_KEY}&callback=initAMap`;

      const scriptEl = document.createElement('script');
      scriptEl.src = mapUrl;
      scriptEl.crossOrigin = 'anonymous';

      if (!window.__PRERENDER_INJECTED) {
        document.body.appendChild(scriptEl);
      }

      initLBS();
    });
  }
  return _loadPromise;
}

export default class AMapHelper {
  static loadPlugin(map, plugins) {
    return new Promise((resolve) => {
      map.plugin(plugins, resolve.bind(this));
    });
  }

  static createMap(domEl, options) {
    return loadMap()
      .then((AMap) => {
        const map = new AMap.Map(domEl, options);
        return map;
      });
  }

  static createMarker(options) {
    return loadMap()
      .then((AMap) => {
        const marker = new AMap.Marker(options);
        return marker;
      });
  }

  static async geoDecode(lng, lat) {
    await loadMap();
    const res = await lbsReGeoCodingSearch({
      locations: [{ lng, lat }],
      extensions: 'all',
      radius: 100,
    },
    {
      serveSort: ['HELLO', 'GAODE'],
    });

    if (res.data.status !== 1
      || !res.data.data.reGeoCodesList
      || res.data.data.reGeoCodesList.length === 0) {
      throw new Error(res.msg);
    } else {
      const { reGeoCodesList = [] } = res.data.data;
      const regeocodeData = reGeoCodesList[0];
      const { formattedAddress, addressComponent } = regeocodeData;
      let adcode = '';
      let citycode = '';
      let name = '';
      const cityName = (Array.isArray(addressComponent.city)
        ? (addressComponent.city[0]) : addressComponent.city);
      const provinceName = addressComponent.province || '';
      if (regeocodeData.addressComponent.adCode) {
        if (Array.isArray(regeocodeData.addressComponent.adCode)) {
          adcode = '';
        } else {
          adcode = regeocodeData.addressComponent.adCode;
        }
      }

      if (regeocodeData.aois && regeocodeData.aois.length > 0) {
        name = regeocodeData.aois[0].name;
      }

      if (regeocodeData.addressComponent.cityCode) {
        if (Array.isArray(regeocodeData.addressComponent.cityCode)) {
          citycode = '';
        } else {
          citycode = regeocodeData.addressComponent.cityCode;
        }
      }

      const formattedRegeocode = {
        ...regeocodeData,
        formatted_address: formattedAddress,
        formattedAddress,
        addressComponent: {
          ...addressComponent,
          adcode,
          citycode,
        },
        adcode,
        citycode,
        latitude: lat,
        longitude: lng,
        name,
        cityName: cityName || provinceName,
      };
      return formattedRegeocode;
    }
  }

  static getPositionByGeo() {
    return new Promise((resolve, reject) => {
      // 兼容处理，如果浏览器不支持timeout配置，超时后，直接reject
      setTimeout(() => {
        reject(new Error('获取地理位置超时'));
      }, 1200);
      if (navigator && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const { longitude, latitude } = position.coords;
          const userAgent = navigator.userAgent.toLowerCase();
          const isAndroid = userAgent.indexOf('android') !== -1;
          const isConverted = userAgent.indexOf('alipay') !== -1 || (userAgent.indexOf('ucbrowser') !== -1 && isAndroid);
          let gcj02Point = { longitude, latitude };
          // 判断是否需要坐标系转换，转换为高德坐标系
          if (!isConverted) {
            // wgs84坐标系，转换为火星坐标系(gcj02)
            const wgs84ToGcj02 = coordtransform.wgs84togcj02(longitude, latitude);
            gcj02Point = { longitude: wgs84ToGcj02[0], latitude: wgs84ToGcj02[1] };
          }
          resolve(gcj02Point);
        }, reject, {
          timeout: 1000,
        });
      } else {
        reject(new Error('不支持原生Geolocation'));
      }
    });
  }

  static async getCityInfo() {
    if (_cityInfoPromise) {
      return _cityInfoPromise;
    }
    await loadMap();
    let geoInfo = null;
    const result = {
      cityCode: '',
      adCode: '',
      city: '',
      longitude: '',
      latitude: '',
    };
    try {
      geoInfo = await this.getPositionByGeo();
    } catch (error) {
      return result;
    }
    result.longitude = String(geoInfo.longitude);
    result.latitude = String(geoInfo.latitude);
    _cityInfoPromise = this.geoDecode(result.longitude, result.latitude);
    const res = await _cityInfoPromise;
    return res;
  }

  static async drivingNavigation({
    origin, destination, strategy, ferry, pathType = 2,
  }) {
    const res = await lbsDriveRouteByServer(
      {
        origin,
        destination,
        strategy,
        ferry,
        pathType,
      },
    );

    if (res.data && +res.data.status === 1) {
      return (res.data.data);
    }
    throw res;
  }

  static zoomFit({
    map, center, visibleLocation, offset = { x: 0, y: 0 },
  }) {
    map.setCenter(center);
    const zoom = map.getZoom();
    const { lng, lat } = visibleLocation;
    const mapSize = map.getSize();
    const mapWidth = mapSize.getWidth();
    const mapHeight = mapSize.getHeight();
    for (let i = zoom; i > 3; i -= 1) {
      const result = map.lngLatToContainer([lng, lat], i);
      if (result.x > offset.x
        && result.y > offset.y
        && result.x < mapWidth - offset.x
        && result.y < mapHeight - offset.y) {
        if (i !== zoom) {
          map.setZoom(i);
        }
        break;
      }
    }
  }

  static getCity(map) {
    return new Promise((resolve, reject) => {
      map.getCity((data) => {
        if (data) {
          resolve(data);
        } else {
          reject();
        }
      });
    });
  }

  /**
   * 将地球坐标(WGS84)转换为高德坐标(火星坐标系GCJ-02)
   * @param WGS84
   */
  static convertLngLat({ lng, lat }) {
    const [tLng, tLat] = coordtransform.wgs84togcj02(Number(lng), Number(lat));
    return Promise.resolve({
      lng: tLng,
      lat: tLat,
    });
  }

  static caculateDistance(pos1, pos2) {
    return loadMap()
      .then((AMap) => new AMap.LngLat(pos1.lng, pos1.lat).distance([pos2.lng, pos2.lat]));
  }
}
